import {categoryColors, categoryIcons} from 'common/categoryAppearance';
import {Ban} from 'lucide-react';
import * as React from 'react';
import {
  categoryIconComponents,
  categorySwatchClasses,
} from 'src/categoryAppearance';
import {CategoryAvatar} from 'src/CategoryAvatar';
import {cn} from 'src/lib/utils';

type Props = {
  name: string;
  icon: string | null;
  color: string | null;
  onIconChange: (icon: string | null) => void;
  onColorChange: (color: string | null) => void;
};

const selectedRing = 'ring-2 ring-primary';

/**
 * Color swatches + icon grid for a category's avatar, with a live
 * preview. null for either value means automatic (hashed color tint /
 * initial letter).
 */
export const CategoryAppearanceInput = (props: Props) => {
  const {name, icon, color, onIconChange, onColorChange} = props;
  const previewName = name.trim() || '?';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <CategoryAvatar category={{name: previewName, icon, color}} />
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            title="Automatic color"
            onClick={() => onColorChange(null)}
            className={cn(
              'border-border text-muted-foreground flex size-6 cursor-pointer items-center justify-center rounded-full border',
              color == null && selectedRing,
            )}
          >
            <Ban className="size-3.5" />
          </button>
          {categoryColors.map((colorName) => (
            <button
              key={colorName}
              type="button"
              title={colorName}
              onClick={() => onColorChange(colorName)}
              className={cn(
                'size-6 cursor-pointer rounded-full',
                categorySwatchClasses[colorName],
                color === colorName && selectedRing,
              )}
            />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-8 gap-1 max-md:grid-cols-6">
        <button
          type="button"
          title="Automatic (initial letter)"
          onClick={() => onIconChange(null)}
          className={cn(
            'text-muted-foreground hover:bg-accent hover:text-foreground flex h-8 cursor-pointer items-center justify-center rounded-md',
            icon == null && `${selectedRing} bg-accent text-foreground`,
          )}
        >
          <span className="text-sm font-semibold">
            {previewName.charAt(0).toUpperCase()}
          </span>
        </button>
        {categoryIcons.map((iconName) => {
          const Icon = categoryIconComponents[iconName];
          return (
            <button
              key={iconName}
              type="button"
              title={iconName}
              onClick={() => onIconChange(iconName)}
              className={cn(
                'text-muted-foreground hover:bg-accent hover:text-foreground flex h-8 cursor-pointer items-center justify-center rounded-md',
                icon === iconName && `${selectedRing} bg-accent text-foreground`,
              )}
            >
              <Icon className="size-4" />
            </button>
          );
        })}
      </div>
    </div>
  );
};
